import type { ReactNode } from 'react';
import type { ArticleStatus, DocumentState } from './types';

// Phase 25 Task 1 — the pieces every KB screen shares.
//
// The rule that governs this file: `--co-machine` means A MACHINE DID THIS and
// nothing else. It appears on embedding state, citations, retrieval scores and
// the Ask panel. It must never appear on an author, a manual edit or a publish
// action — the moment it does, the reader can no longer tell what the assistant
// touched.

const MONO = 'font-mono';

/**
 * `SEARCH INDEX` — two honest states (phase 27 §4).
 *
 * Renamed from `USED BY THE AI`, because a boolean cannot answer that
 * question. Being in the index is not the same as being used, and the old
 * label promised a guarantee the data cannot make. It CAN answer "is this in
 * the index", so that is what the column asks.
 *
 * The defect is bordered and the normal state is not — the same distinction
 * that separated NOT EMBEDDED from NEVER CITED, and the reason both states
 * survive a greyscale check: filled square versus bordered chip.
 */
export function IndexState({ indexed }: { indexed: boolean }) {
  if (indexed) {
    return (
      <span
        className={`${MONO} inline-flex items-center gap-[6px] text-[11px] font-medium tracking-[.06em]`}
        style={{ color: 'var(--co-machine-ink)' }}
        title="In the search index. Whether the assistant has actually quoted it is not tracked."
      >
        <span
          className="h-[6px] w-[6px] flex-none"
          style={{ background: 'var(--co-machine)' }}
          aria-hidden="true"
        />
        INDEXED
      </span>
    );
  }
  return (
    <span
      className={`${MONO} inline-flex items-center border px-[6px] py-[3px] text-[10.5px] font-semibold tracking-[.08em]`}
      style={{ borderColor: 'var(--co-terracotta)', color: 'var(--co-terracotta-ink)' }}
      title="Not in the search index — the assistant cannot retrieve this article at all."
    >
      NOT INDEXED
    </span>
  );
}

export function StatusChip({ status }: { status: ArticleStatus }) {
  const style =
    status === 'published'
      ? {
          borderColor: 'var(--co-green-line)',
          background: 'var(--co-green-wash)',
          color: 'var(--co-green-ink)',
        }
      : status === 'draft'
        ? { borderColor: 'var(--co-line-strong)', color: 'var(--co-ink-2)' }
        : { borderColor: 'var(--co-line)', color: 'var(--co-ink-3)' };
  return (
    <span
      className={`${MONO} inline-flex items-center border px-[6px] py-[3px] text-[9.5px] font-semibold tracking-[.12em]`}
      style={style}
    >
      {status.toUpperCase()}
    </span>
  );
}

export function VisibilityChip({
  visibility,
}: {
  visibility: 'public' | 'internal' | 'inactive';
}) {
  const style =
    visibility === 'public'
      ? {
          borderColor: 'var(--co-green-line)',
          background: 'var(--co-green-wash)',
          color: 'var(--co-green-ink)',
        }
      : visibility === 'internal'
        ? { borderColor: 'var(--co-line-strong)', color: 'var(--co-ink-2)' }
        : { borderColor: 'var(--co-line)', color: 'var(--co-ink-3)' };
  return (
    <span
      className={`${MONO} inline-flex items-center border px-[6px] py-[3px] text-[9.5px] font-semibold tracking-[.12em]`}
      style={style}
    >
      {visibility.toUpperCase()}
    </span>
  );
}

/** Four states, and a failure states its CAUSE rather than its code (§7). */
export function DocStateChip({ state }: { state: DocumentState }) {
  if (state.kind === 'excluded') return null;

  if (state.kind === 'extracting') {
    return (
      <span
        className={`${MONO} inline-flex items-center gap-2 border px-[6px] py-[3px] text-[9.5px] font-semibold tracking-[.12em]`}
        style={{
          borderColor: 'var(--co-machine-line)',
          background: 'var(--co-machine-wash)',
          color: 'var(--co-machine-ink)',
        }}
      >
        {/* motion-safe is the reduced-motion gate: the dot is present and
            still rather than absent. */}
        <span
          className="h-[5px] w-[5px] flex-none motion-safe:animate-pulse"
          style={{ background: 'var(--co-machine)' }}
          aria-hidden="true"
        />
        {state.done !== undefined && state.total !== undefined
          ? `EXTRACTING ${state.done}/${state.total}`
          : 'EXTRACTING'}
      </span>
    );
  }

  const style =
    state.kind === 'ready'
      ? {
          borderColor: 'var(--co-green-line)',
          background: 'var(--co-green-wash)',
          color: 'var(--co-green-ink)',
        }
      : {
          borderColor: 'var(--co-burgundy-line)',
          background: 'var(--co-burgundy-wash)',
          color: 'var(--co-burgundy)',
        };
  return (
    <span
      className={`${MONO} inline-flex items-center border px-[6px] py-[3px] text-[9.5px] font-semibold tracking-[.12em]`}
      style={style}
    >
      {state.kind.toUpperCase()}
    </span>
  );
}

/** Section rule: mono label, hairline, optional right action. */
export function SectionRule({ label, action }: { label: string; action?: ReactNode }) {
  return (
    <div className="flex items-center gap-3">
      <span className={`${MONO} flex-none text-[9.5px] font-semibold tracking-[.2em] text-[var(--co-ink-4)]`}>
        {label}
      </span>
      <div className="h-px flex-1 bg-[var(--co-line-subtle)]" />
      {action}
    </div>
  );
}
